/**
 * 색상 테마 팔레트 선택기 컴포넌트
 * ALT+T로 표시/숨김 토글
 */

import { motion, AnimatePresence } from 'framer-motion';
import { HiColorSwatch, HiCheck, HiX } from 'react-icons/hi';
import { colorPalettes, applyColorPalette } from '../theme/colorPalettes';

export default function ThemeSelector({ currentTheme, onThemeChange, onClose }) {
  return (
    <AnimatePresence>
      <motion.div
        className="relative"
        initial={{ opacity: 0, scale: 0.9, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        {/* 닫기 버튼이 있는 헤더 */}
        <div className="flex items-center justify-between mb-2">
          <motion.div
            className="flex items-center gap-2 px-4 py-2 rounded-xl shadow-lg"
            style={{
              background: 'var(--color-surface)',
              border: '2px solid var(--color-primary)',
              color: 'var(--color-text)',
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <HiColorSwatch className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
            <span className="text-sm font-semibold">테마 선택</span>
          </motion.div>
          <motion.button
            onClick={onClose}
            className="p-2 rounded-lg shadow-lg transition-all"
            style={{
              background: 'var(--color-surface)',
              border: '2px solid var(--color-primary)',
              color: 'var(--color-text)',
            }}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            title="닫기 (ESC)"
          >
            <HiX className="w-5 h-5" />
          </motion.button>
        </div>

        {/* 팔레트 선택 메뉴 */}
        <motion.div
          className="w-64 rounded-xl shadow-2xl border-2 overflow-hidden"
          style={{
            background: 'var(--color-surface)',
            borderColor: 'var(--color-primary)',
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="p-4">
            <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--color-text)' }}>색상 팔레트 선택</h3>
            <p className="text-xs mb-3 opacity-70" style={{ color: 'var(--color-text-secondary)' }}>
              ALT+T로 다시 열기
            </p>
            <div className="grid grid-cols-1 gap-2">
              {Object.entries(colorPalettes).map(([key, palette]) => (
                <motion.button
                  key={key}
                  onClick={() => {
                    applyColorPalette(palette);
                    onThemeChange(key);
                  }}
                  className="flex items-center justify-between p-3 rounded-lg border-2 transition-all"
                  style={{
                    borderColor: currentTheme === key ? 'var(--color-primary)' : 'transparent',
                    background: currentTheme === key ? 'rgba(var(--color-primary-rgb), 0.1)' : 'transparent',
                  }}
                  whileHover={{ scale: 1.02, x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  onHoverStart={(e) => {
                    if (currentTheme !== key) {
                      e.currentTarget.style.borderColor = 'var(--color-accent)';
                      e.currentTarget.style.background = 'rgba(var(--color-accent-rgb), 0.05)';
                    }
                  }}
                  onHoverEnd={(e) => {
                    if (currentTheme !== key) {
                      e.currentTarget.style.borderColor = 'transparent';
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    {/* 색상 미리보기 */}
                    <div className="flex gap-1">
                      <div
                        className="w-6 h-6 rounded-full border-2 border-white shadow-md"
                        style={{ backgroundColor: palette.primary }}
                      />
                      <div
                        className="w-6 h-6 rounded-full border-2 border-white shadow-md"
                        style={{ backgroundColor: palette.secondary }}
                      />
                      <div
                        className="w-6 h-6 rounded-full border-2 border-white shadow-md"
                        style={{ backgroundColor: palette.accent }}
                      />
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>{palette.name}</span>
                  </div>
                  {currentTheme === key && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      style={{ color: 'var(--color-primary)' }}
                    >
                      <HiCheck className="w-5 h-5" />
                    </motion.div>
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
